import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Subscription } from 'rxjs';

import { ModifiedErrorStateMatcher } from '../../extensions/errorstatematcher/errorstatematcher';

@Component({
    selector: 'material-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    standalone: false,
})
export class MaterialSelectComponent implements OnInit, DoCheck, OnChanges, OnDestroy {
    @Input() options: any[];
    @Input() createOptionRequired = false;
    @Input() placeholder: string;
    @Input() name: string;
    @Input() title: string;
    @Input() model: any;
    @Input() item: string;
    @Input() id: string;
    @Input() classList: string[];
    @Input() required: boolean;
    @Input() errors: any[];
    @Input() warnings: any[];
    @Input() disabled: boolean;
    @Input() optionDisplayNameProperty?: string;
    @Input() optionValueProperty?: string;
    @Input() emitFirstEvent?: boolean;
    @Input() noSelectIfValidOptionSelected: boolean;
    @Input() hideSelectIfValueExists: boolean;
    @Input() description: string;
    @Input() isSearchNotRequired: boolean;
    @Input() isMultiple: boolean;
    @Input() appearance: MatFormFieldAppearance;
    @Input() noLabel: boolean;
    @Input() hint: string;
    @Input() noHints = false;
    @Input() emitEvent = false;
    @Input() tailwindClasses: string[];
    @Input() panelClass: string | string[] | Set<string> | { [key: string]: any };

    @Output() createOptionCallBack = new EventEmitter();
    @Output() editOptionCallBack = new EventEmitter();
    @Output() modelChange = new EventEmitter();
    @Output() optionData = new EventEmitter();
    @Output() debounceEventHandler = new EventEmitter();

    matcher = new ModifiedErrorStateMatcher();
    control = new UntypedFormControl('');
    valueSubscription: Subscription;
    searchColumns = undefined;
    selectedOption: any;
    // Computed properties for template bindings
    selectedOptionDisplay = '';
    searchPlaceholder = '';
    pleaseSelectText = '';
    noResultsText = '';
    requiredText = '';
    private lastErrorsLength: number | undefined;

    constructor() {}

    ngDoCheck(): void {
        // Lightweight sync: if the errors array length changed (possibly mutated in place), update control state
        const currentLen = Array.isArray(this.errors) ? this.errors.length : 0;
        if (this.lastErrorsLength !== currentLen) {
            this.updateFormErrors();
        }
        if (this.model?.length > 0 && this.options?.length > 0 && !this.selectedOption) {
            this.prepareSelectedOption();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.model) {
            if (this.control.value !== changes.model?.currentValue) {
                this.control.setValue(this.model, { emitEvent: this.emitEvent || false });
            }
            this.prepareSelectedOption();
        }
        if (changes.disabled) {
            if (this.control.value !== changes.model?.currentValue) {
                this.control.setValue(this.model, { emitEvent: this.emitEvent || false });
            }
            if (this.disabled === true) {
                this.control.disable();
            } else {
                this.control.enable();
            }
        }
        if (changes?.options?.currentValue) {
            this.prepareSelectedOption();
        }
        if (changes.errors) {
            this.updateFormErrors();
        }
        this.updateComputedProperties();
    }

    modelChanged = event => {
        this.model = event;
        this.modelChange.next(this.model);
        this.debounceEventHandler.emit(event);
    };

    sendOptionsData = option => {
        this.optionData.next(option);
    };

    private prepareSelectedOption() {
        if (!this.options || !this.model) {
            this.selectedOption = undefined;
            return undefined;
        }

        if (this.isMultiple && Array.isArray(this.model)) {
            // For multiple selection, find all selected options
            this.selectedOption = this.options.filter(
                option => this.model.includes(option?.[this.optionValueProperty || 'value']) || this.model.includes(option)
            );
            return this.selectedOption;
        } else {
            // For single selection
            this.selectedOption = this.options.find(
                option => option?.[this.optionValueProperty || 'value'] === this.model || option === this.model
            );
            return this.selectedOption;
        }
    }

    private updateFormErrors(): void {
        // Sync external errors input to form control state without heavy comparisons
        if (!this.errors || (Array.isArray(this.errors) && this.errors.length === 0)) {
            if (this.control.getError('errors') !== undefined) {
                this.control.setErrors(undefined);
            }
            this.lastErrorsLength = 0;
        } else {
            this.control.setErrors({ errors: this.errors });
            this.lastErrorsLength = this.errors.length;
        }
    }

    private updateComputedProperties(): void {
        // Update computed display properties to avoid template expressions
        this.updateSelectedOptionDisplay();
        // Note: Translation strings will be handled in template with translate pipe or function calls
    }

    private updateSelectedOptionDisplay(): void {
        if (!this.selectedOption) {
            this.selectedOptionDisplay = '';
            return;
        }

        const displayValue = this.optionDisplayNameProperty
            ? this.selectedOption?.[this.optionDisplayNameProperty]
            : this.selectedOption?.displayName !== undefined
            ? this.selectedOption.displayName
            : this.selectedOption;

        this.selectedOptionDisplay = displayValue || '';
    }

    getOptionDisplayText(option: any): string {
        return this.optionDisplayNameProperty
            ? option?.[this.optionDisplayNameProperty]
            : option?.displayName !== undefined
            ? option.displayName
            : option;
    }

    getOptionValue(option: any): any {
        return this.optionValueProperty !== undefined
            ? option?.[this.optionValueProperty]
            : option?.value !== undefined
            ? option.value
            : option;
    }

    ngOnInit() {
        this.valueSubscription = this.control.valueChanges.subscribe(event => this.modelChanged(event));
        this.prepareSelectedOption();
        this.updateComputedProperties();
        this.updateFormErrors();
        if (this.model !== undefined && this.emitFirstEvent) {
            this.modelChanged(this.model);
        }
    }

    getTooltipTitle(): string {
        if (this.errors && this.errors.length > 0) {
            return Array.isArray(this.errors) ? this.errors.join(', ') : String(this.errors);
        }

        const targetVal = this.model !== undefined && this.model !== null && this.model !== '' ? this.model : this.title;

        if (this.isMultiple && Array.isArray(this.selectedOption)) {
            const displays = this.selectedOption.map(opt => this.getOptionDisplayText(opt)).filter(Boolean);
            if (displays.length > 0) {
                return displays.join(', ');
            }
        } else if (this.selectedOption) {
            const disp = this.getOptionDisplayText(this.selectedOption);
            if (disp !== undefined && disp !== null && disp !== '') {
                return String(disp);
            }
        } else if (
            this.options &&
            this.options.length > 0 &&
            targetVal !== undefined &&
            targetVal !== null &&
            targetVal !== '' &&
            targetVal !== 'undefined'
        ) {
            const valProp = this.optionValueProperty || 'value';
            const found = this.options.find(opt => opt?.[valProp] === targetVal || opt === targetVal);
            if (found) {
                const disp = this.getOptionDisplayText(found);
                if (disp !== undefined && disp !== null && disp !== '') {
                    return String(disp);
                }
            }
        }

        if (typeof this.title === 'string' && this.title !== 'undefined' && this.title !== '' && this.title !== this.model) {
            return this.title;
        }

        return '';
    }

    ngOnDestroy(): void {
        if (this.valueSubscription) {
            this.valueSubscription.unsubscribe();
        }
    }
}
